import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  ShieldCheck, ShieldAlert, BadgeCheck, Eye, Lock, Info, ChevronRight, AlertCircle, Check,
} from 'lucide-react';
import { logger } from './utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_LABEL = {
  signal_only: 'Signal only — never released',
  pre_interview: 'Released before interview',
  pre_offer: 'Released before offer',
  post_acceptance: 'Released after offer accepted',
};

const BASIS_SHORT = {
  legal_obligation: 'Legal obligation',
  contract_necessity: 'Contract necessity',
  consent: 'Your consent',
  legitimate_interest: 'Legitimate interest',
};

// ---------------------------------------------------------------------------
// CANDIDATE — Required Pack panel for one application
// ---------------------------------------------------------------------------
export function CandidatePackPanel({ applicationId }) {
  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyItem, setBusyItem] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/me/applications/${applicationId}/pack`);
      setPack(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not load required pack');
    } finally { setLoading(false); }
  }, [applicationId]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (cat, enabled) => {
    setBusyItem(cat);
    try {
      await axios.post(`${API}/me/applications/${applicationId}/pack/signal`, {
        item_category: cat, enabled,
      });
      await load();
    } catch (err) {
      logger.error('toggle signal', err);
      window.alert(err.response?.data?.detail || 'Failed to update signal');
    } finally { setBusyItem(''); }
  };

  if (loading) return <div className="bg-white rounded-lg shadow p-5 h-40 animate-pulse" data-testid="pack-panel-loading" />;
  if (error) return (
    <div className="bg-white rounded-lg shadow p-5 text-sm text-rose-600 flex gap-2" data-testid="pack-panel-error">
      <AlertCircle className="w-4 h-4" />{error}
    </div>
  );
  if (!pack) return null;

  return (
    <div className="bg-white rounded-lg shadow p-5" data-testid="candidate-pack-panel">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-semibold flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-emerald-600" /> Required pack — {pack.role_title || 'this role'}
        </h3>
        <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded" data-testid="pack-ready-count">
          {pack.ready_count} of {pack.total_count} ready
        </span>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        You control what the employer sees at each stage. Toggling a signal tells them
        "Vault Hire has this verified" — the actual document is only released at the
        stage shown.
      </p>

      {pack.items.length === 0 && (
        <div className="text-sm text-gray-500 py-4" data-testid="pack-empty">
          The employer hasn't defined a requirement pack for this role yet.
        </div>
      )}

      <ul className="space-y-2" data-testid="pack-items">
        {pack.items.map((it) => {
          const onSignal = it.signal_enabled;
          const haveIt = it.have_in_vault === true;
          const canHave = it.auto_backed && haveIt;
          const selfAttest = !it.auto_backed;
          return (
            <li key={it.category} data-testid={`pack-item-${it.category}`} className="p-3 border rounded-lg">
              <div className="flex items-start gap-3">
                <div className="mt-0.5">
                  {canHave ? <BadgeCheck className="w-5 h-5 text-emerald-600" aria-label="In vault" />
                    : it.auto_backed ? <ShieldAlert className="w-5 h-5 text-amber-500" aria-label="Not yet in vault" />
                    : <Info className="w-5 h-5 text-blue-500" aria-label="Self-attest" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="font-medium text-sm">
                      {it.label}
                      {it.mandatory && <span className="ml-2 text-xs text-rose-600">required</span>}
                      {it.lookback_years && <span className="ml-2 text-xs text-purple-700">{it.lookback_years}-year lookback</span>}
                    </p>
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={onSignal}
                        disabled={busyItem === it.category}
                        onChange={(e) => toggle(it.category, e.target.checked)}
                        data-testid={`pack-signal-${it.category}`}
                        className="w-4 h-4"
                      />
                      <span className="text-xs font-medium">{onSignal ? 'Signal: ON' : 'Signal: off'}</span>
                    </label>
                  </div>
                  <div className="mt-1 text-xs text-gray-600 flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1"><Lock className="w-3 h-3" /> {STAGE_LABEL[it.release_stage] || it.release_stage}</span>
                    <span className="text-gray-400">·</span>
                    <span>{BASIS_SHORT[it.lawful_basis] || it.lawful_basis}</span>
                  </div>
                  {it.note && <p className="mt-1 text-xs text-purple-700">{it.note}</p>}
                  {it.auto_backed && !haveIt && (
                    <p className="mt-1 text-xs text-amber-700">⚠ Nothing matching this in your vault yet — add it on the vault tab.</p>
                  )}
                  {selfAttest && (
                    <p className="mt-1 text-xs text-blue-600">Self-attested — by signalling ON, you confirm you can produce this on request.</p>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// EMPLOYER — Manifest builder for a requisition
// ---------------------------------------------------------------------------
export function EmployerManifestBuilder({ requisitionId, onSaved }) {
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [bases, setBases] = useState([]);
  const [stages, setStages] = useState([]);
  const [items, setItems] = useState([]);
  const [flags, setFlags] = useState({ regulated_role: false, safeguarding_role: false, finra_regulated: false });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddPicker, setShowAddPicker] = useState(false);

  const load = useCallback(async () => {
    try {
      const [tpls, current] = await Promise.all([
        axios.get(`${API}/manifest/templates`),
        axios.get(`${API}/requisitions/${requisitionId}/manifest`),
      ]);
      setTemplates(tpls.data.templates || []);
      setCategories(tpls.data.categories || []);
      setBases(tpls.data.lawful_bases || []);
      setStages(tpls.data.release_stages || []);
      setItems(current.data.items || []);
      setFlags({
        regulated_role: !!current.data.flags?.regulated_role,
        safeguarding_role: !!current.data.flags?.safeguarding_role,
        finra_regulated: !!current.data.flags?.finra_regulated,
      });
    } catch (err) { logger.error('manifest load', err); }
  }, [requisitionId]);

  useEffect(() => { load(); }, [load]);

  const applyTemplate = (tplId) => {
    const t = templates.find((x) => x.id === tplId);
    if (!t) return;
    setItems(JSON.parse(JSON.stringify(t.items)));
    // Banking SMF & cert regime → auto-flag regulated; US advisor → finra
    if (tplId.includes('smf') || tplId.includes('cert')) setFlags((f) => ({ ...f, regulated_role: true }));
    if (tplId.includes('us_financial')) setFlags((f) => ({ ...f, finra_regulated: true }));
    setSuccess(`Loaded template: ${t.name}`);
    setTimeout(() => setSuccess(''), 3000);
  };

  const updateItem = (idx, patch) => {
    setItems((cur) => cur.map((it, i) => i === idx ? { ...it, ...patch } : it));
  };

  const removeItem = (idx) => setItems((cur) => cur.filter((_, i) => i !== idx));

  const addItem = (category) => {
    if (items.some((i) => i.category === category)) {
      setError(`${category} already in manifest`);
      return;
    }
    setItems((cur) => [...cur, {
      category, mandatory: true, lawful_basis: 'legal_obligation', release_stage: 'post_acceptance', min_count: 1,
    }]);
    setShowAddPicker(false);
  };

  const save = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      await axios.put(`${API}/requisitions/${requisitionId}/manifest`, {
        items, ...flags,
      });
      setSuccess('Manifest saved. Candidates applying to this role will see the pack on their dashboard.');
      onSaved?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-lg shadow p-5" data-testid="manifest-builder">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-blue-600" /> Requirement manifest</h3>
        <span className="text-xs text-gray-500">{items.length} item{items.length === 1 ? '' : 's'}</span>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Declare exactly what this role needs — with a lawful basis per item.
        Candidates will see a stage-locked checklist; nothing leaves their vault before the
        release stage you choose. <strong>Privacy by design — UK GDPR Art. 5(1)(c).</strong>
      </p>

      {/* Templates */}
      <div className="mb-4">
        <p className="text-xs font-medium mb-2">Start from a template</p>
        <div className="flex flex-wrap gap-2" data-testid="manifest-templates">
          {templates.map((t) => (
            <button
              key={t.id}
              onClick={() => applyTemplate(t.id)}
              data-testid={`manifest-template-${t.id}`}
              className="px-3 py-1.5 text-xs bg-slate-100 hover:bg-blue-100 rounded border border-slate-200"
              title={t.description}
            >
              {t.name}
            </button>
          ))}
        </div>
      </div>

      {/* Flags */}
      <div className="mb-4 p-3 bg-slate-50 border rounded">
        <p className="text-xs font-medium mb-2">Role flags (gate restricted checks)</p>
        <div className="flex flex-wrap gap-4 text-xs">
          {[
            ['regulated_role', 'Regulated role (FCA/PRA) — unlocks credit & DBS Standard'],
            ['safeguarding_role', 'Safeguarding role — unlocks DBS Enhanced'],
            ['finra_regulated', 'FINRA-regulated (US) — unlocks U4 & fingerprint'],
          ].map(([k, lbl]) => (
            <label key={k} className="inline-flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={flags[k]}
                onChange={(e) => setFlags({ ...flags, [k]: e.target.checked })}
                data-testid={`manifest-flag-${k}`}
              />
              <span>{lbl}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Items */}
      <ul className="space-y-2 mb-4" data-testid="manifest-items">
        {items.map((it, idx) => {
          const spec = categories.find((c) => c.id === it.category) || {};
          const restricted = spec.restricted;
          return (
            <li key={`${it.category}-${idx}`} data-testid={`manifest-item-${it.category}`} className="p-3 border rounded">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                  <p className="text-sm font-medium">
                    {spec.label || it.category}
                    {restricted && <span className="ml-2 text-xs text-amber-700">restricted</span>}
                  </p>
                  <p className="text-xs text-gray-500 font-mono">{it.category}</p>
                </div>
                <button
                  onClick={() => removeItem(idx)}
                  data-testid={`manifest-remove-${it.category}`}
                  className="text-xs text-rose-600 hover:underline"
                >Remove</button>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <label>
                  <span className="block mb-1 text-gray-600">Lawful basis</span>
                  <select
                    value={it.lawful_basis}
                    onChange={(e) => updateItem(idx, { lawful_basis: e.target.value })}
                    data-testid={`manifest-basis-${it.category}`}
                    className="w-full px-2 py-1 border rounded"
                  >
                    {bases.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
                  </select>
                </label>
                <label>
                  <span className="block mb-1 text-gray-600">Release stage</span>
                  <select
                    value={it.release_stage}
                    onChange={(e) => updateItem(idx, { release_stage: e.target.value })}
                    data-testid={`manifest-stage-${it.category}`}
                    className="w-full px-2 py-1 border rounded"
                  >
                    {stages.map((s) => <option key={s} value={s}>{STAGE_LABEL[s] || s}</option>)}
                  </select>
                </label>
                <label className="inline-flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={!!it.mandatory}
                    onChange={(e) => updateItem(idx, { mandatory: e.target.checked })}
                  />
                  <span>Mandatory</span>
                </label>
                {it.category === 'regulatory_reference' && (
                  <label>
                    <span className="block mb-1 text-gray-600">Lookback years</span>
                    <input
                      type="number" min={1} max={10}
                      value={it.lookback_years || 6}
                      onChange={(e) => updateItem(idx, { lookback_years: parseInt(e.target.value, 10) || 6 })}
                      className="w-full px-2 py-1 border rounded"
                    />
                  </label>
                )}
              </div>
            </li>
          );
        })}
        {items.length === 0 && (
          <li className="text-sm text-gray-500 py-3 text-center" data-testid="manifest-empty">No items yet — load a template above or add items below.</li>
        )}
      </ul>

      {showAddPicker ? (
        <div className="mb-4 p-3 bg-blue-50 rounded border" data-testid="manifest-picker">
          <p className="text-xs font-medium mb-2">Add an item</p>
          <div className="grid grid-cols-2 gap-1 max-h-60 overflow-y-auto">
            {categories.map((c) => {
              const flagOk = !c.restricted || flags[c.requires_flag];
              return (
                <button
                  key={c.id}
                  onClick={() => addItem(c.id)}
                  disabled={!flagOk}
                  data-testid={`manifest-add-${c.id}`}
                  className="text-xs text-left px-2 py-1.5 bg-white border rounded hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  title={!flagOk ? `Set role flag "${c.requires_flag}" first` : c.label}
                >
                  {c.label} {c.restricted && '🔒'}
                </button>
              );
            })}
          </div>
          <button onClick={() => setShowAddPicker(false)} className="mt-2 text-xs text-gray-500">Cancel</button>
        </div>
      ) : (
        <button
          onClick={() => setShowAddPicker(true)}
          data-testid="manifest-show-picker-btn"
          className="mb-4 text-xs text-blue-600 hover:underline"
        >+ Add item</button>
      )}

      {error && <p className="text-xs text-rose-600 mb-2" data-testid="manifest-error">{error}</p>}
      {success && <p className="text-xs text-emerald-700 mb-2" data-testid="manifest-success">{success}</p>}

      <button
        onClick={save}
        disabled={saving}
        data-testid="manifest-save-btn"
        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded disabled:opacity-50"
      >
        {saving ? 'Saving…' : 'Save manifest'}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// EMPLOYER — Pack readiness badge for a single application (cockpit-side)
// ---------------------------------------------------------------------------
export function EmployerPackReadiness({ applicationId, compact = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/employer/applications/${applicationId}/pack-readiness`)
      .then((r) => { if (mounted) setData(r.data); })
      .catch((e) => logger.error('pack readiness', e))
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [applicationId]);

  if (loading) return <span className="text-xs text-gray-400">…</span>;
  if (!data || data.total_count === 0) return null;

  const ratio = data.ready_count / data.total_count;
  const color = ratio === 1 ? 'bg-emerald-100 text-emerald-700' : ratio >= 0.5 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600';

  if (compact) {
    return (
      <span className={`text-xs px-2 py-0.5 rounded ${color}`} data-testid={`pack-badge-${applicationId}`}>
        Pack {data.ready_count}/{data.total_count}
      </span>
    );
  }

  return (
    <div className="bg-white border rounded p-3" data-testid="employer-pack-readiness">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-between w-full text-sm font-medium"
        data-testid="pack-readiness-toggle"
      >
        <span className="flex items-center gap-2"><Eye className="w-3.5 h-3.5" /> Candidate pack signals</span>
        <span className={`text-xs px-2 py-0.5 rounded ${color}`}>{data.ready_count}/{data.total_count} ready</span>
      </button>
      {open && (
        <ul className="mt-3 space-y-1 text-xs">
          {data.items.map((it) => (
            <li key={it.category} className="flex items-center justify-between gap-2">
              <span>{it.label}</span>
              {it.released ? (
                <span className="text-emerald-700 inline-flex items-center gap-0.5"><Check className="w-3 h-3" /> Released</span>
              ) : it.signal === 'verified_ready' ? (
                <span className="text-blue-700">Signal: ready</span>
              ) : (
                <span className="text-gray-400">No signal yet</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
